import { FunctionComponent } from 'react'

import styled, { css, useTheme } from 'styled-components'

import { desktopCSS, mobileAndTabletCSS } from '@measures/responsive'
import useBurgerMenuVisibility from '@hooks/useBurgerMenuVisibility'
import useIsImmersiveHeaderActive from '@hooks/useIsImmersiveHeaderActive'
import useNavigation from '@hooks/useNavigation'
import useToggleMenuVisibility from '@hooks/useToggleMenuVisibility'
import IconButton from '@components/Buttons/IconButton'

import FirstLevelItem from './FirstLevelItem'
import NavigationHeader from './NavigationHeader'

interface NavigationProps {
  isVisible: boolean
}

const Navigation = styled.nav<NavigationProps>`
  ${({
    isVisible,
    theme: {
      colors: { fillDefault },
      animation: {
        transition: { default: defaultTransition },
      },
    },
  }) => css`
    z-index: 2;
    position: fixed;
    top: 0;
    bottom: 0;
    width: calc(100% - 78px);
    ${desktopCSS(css`
      width: 400px;
    `)}
    overflow-y: auto;
    background-color: ${fillDefault};
    padding-top: 0;
    left: 0;
    transition: ${defaultTransition};
    transform: translate3d(${isVisible ? 0 : '-100%'}, 0, 0);
  `}
`

const MenuContainer = styled.div`
  display: grid;
  grid-auto-flow: row;
`

interface OverlayProps {
  isVisible: boolean
}

const Overlay = styled.div<OverlayProps>`
  ${({
    theme: {
      animation: {
        transition: { fast: fastTransition, default: defaultTransition },
      },
      colors: { staticBlack },
    },
    isVisible,
  }) => css`
    position: absolute;
    top: 0;
    opacity: 0;
    transition: opacity ${fastTransition};
    width: 100vw;
    height: 100vh;
    left: -100vw;
    right: 0;
    bottom: 0;
    z-index: 1;
    background: ${staticBlack};
    display: none;

    ${isVisible &&
    css`
      display: block;
      opacity: 0.7;
      left: 0;
      transition: opacity ${defaultTransition};
    `}
  `}
`

const StyledIconButton = styled(IconButton)<{
  isImmersiveHeaderActive: boolean
}>`
  ${({
    theme: {
      colors: { staticWhite, iconBrand },
    },
    isImmersiveHeaderActive,
  }) => css`
    ${desktopCSS(css`
      width: 40px;
      height: 40px;
    `)};

    ${isImmersiveHeaderActive &&
    css`
      ${mobileAndTabletCSS(css`
        > svg {
          fill: ${staticWhite};
        }
      `)}
    `}

    &:hover {
      > svg {
        fill: ${iconBrand};
      }
    }
  `}
`

const BurgerMenu: FunctionComponent = () => {
  const theme = useTheme()
  const navigationItems = useNavigation()
  const burgerMenuVisible = useBurgerMenuVisibility()
  const isImmersiveHeaderActive = useIsImmersiveHeaderActive()

  const { openBurgerMenu, closeBurgerMenu } = useToggleMenuVisibility()

  return (
    <>
      <StyledIconButton
        ariaLabel="Menu"
        onClick={openBurgerMenu}
        iconName="bars"
        iconSize={24}
        buttonSize={48}
        iconColor={theme.colors.iconDefault}
        isImmersiveHeaderActive={isImmersiveHeaderActive}
      />
      <Navigation isVisible={burgerMenuVisible}>
        <MenuContainer>
          <NavigationHeader onClose={() => closeBurgerMenu(false)} />
          <div>
            {navigationItems.map((item) => (
              <FirstLevelItem key={item.title} {...item} />
            ))}
          </div>
        </MenuContainer>
      </Navigation>
      <Overlay
        isVisible={burgerMenuVisible}
        onClick={() => closeBurgerMenu(false)}
      />
    </>
  )
}

export default BurgerMenu
